import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService, CredentialsService } from '@app/auth';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { LoginComponent } from '@app/auth/login.component';
import { LocationStrategy, ViewportScroller } from '@angular/common';
import { Logger, UntilDestroy, untilDestroyed } from '@shared';
import { UserDTO, WithdrawalProductType } from '@generated/generatedEntities';
import { MatMenuTrigger } from '@angular/material/menu';
import { GlobalModalService } from '@app/core/services/global_modal/global-modal.service';
import { UserService } from '@app/core/services/User/user.service';
import { debounceTime, take } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { ScrollService } from '@app/calculators/services/scroller-service/scroll.service';
import { PayloadDebugComponent } from '@app/calculators/components/payload-debug/payload-debug.component';
import { TRANSLATION_LANGUAGE } from '@app/transloco-root.module';
import { environment } from '@env/environment';
import { Language } from '@generated/extended';
import { toSignal } from '@angular/core/rxjs-interop';

const log = new Logger('Header');
const PREVIOUS_LANG_KEY = 'previous-lang';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['global', 'products'],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class HeaderComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authenticationService = inject(AuthenticationService);
  private credentialsService = inject(CredentialsService);
  private dialog = inject(MatDialog);
  public url = inject(LocationStrategy);
  private translocoService = inject(TranslocoService);
  private cdr = inject(ChangeDetectorRef);
  private globalModalService = inject(GlobalModalService);
  private viewportScroller = inject(ViewportScroller);
  private _scrollService = inject(ScrollService);
  private userService = inject(UserService);
  user: UserDTO | null = null;
  licenseIssue: WithdrawalProductType[] = [];
  @ViewChildren(MatMenuTrigger) trigger!: QueryList<MatMenuTrigger>;
  makeDarker = false;
  enableTranslation = false;
  environment = environment;
  isAdminBool = false;
  activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  ngOnInit() {
    this.init();
    this.setHeaderDark();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  init() {
    this.cdr.detectChanges();

    this.authenticationService.getAuthenticationState().subscribe({
      next: (value) => {
        this.user = value;
        if (this.user) {
          this.isAdminBool = this.user.authorities.includes('ROLE_ADMIN');
          this.licenseIssue = this.user?.withdrawalProductTypes;
        }
      },
    });

    this.initializeAccent();
  }

  initializeAccent() {
    if (environment.production) return;

    if (sessionStorage.getItem(PREVIOUS_LANG_KEY) === null)
      sessionStorage.setItem(PREVIOUS_LANG_KEY, this.translocoService.getActiveLang());
    if (this.enableTranslation) setTimeout(() => this.injectAccent(), 1000);
  }

  injectAccent() {
    if (environment.production) return;
    this.languageChange(TRANSLATION_LANGUAGE);

    // Dispatch event on new 'accent:ready' event
    document.addEventListener(
      'accent:ready',
      () => {
        document.dispatchEvent(
          new CustomEvent('accent:init', {
            detail: {
              projectId: environment.accentProjectId,
              origin: environment.accentUrl,
              hideLoading: true,
            },
          }),
        );
      },
      { once: true },
    );
    const s = document.createElement('script');
    s.src = `${environment.accentUrl}/static/jipt/index.js`;
    document.body.append(s);
  }

  toggleTranslation() {
    const prevLang: Language = (sessionStorage.getItem(PREVIOUS_LANG_KEY) as Language) || 'de-CH';
    this.enableTranslation = !this.enableTranslation;

    if (this.enableTranslation) this.injectAccent();
    else {
      this.languageChange(prevLang);
    }
  }

  setHeaderDark() {
    this._scrollService
      .getScrollY()
      .pipe(debounceTime(100), untilDestroyed(this))
      .subscribe({
        next: (scrollY: number) => {
          this.makeDarker = scrollY > 0;
        },
        error: (err: any) => {
          log.error('error setting darkmode: ', err);
        },
      });
  }

  openMenu(event: any) {
    this.trigger.toArray().forEach((item: MatMenuTrigger) => {
      item.closeMenu();
      if (item.menuData['trigger'] == event.currentTarget.getAttribute('data-value')) {
        item.openMenu();
      }
    });
  }

  logout() {
    this.authenticationService
      .logout()
      .pipe()
      .subscribe({
        next: () => {
          this.router.navigate([this.route.snapshot.queryParams['redirect'] || ''], { replaceUrl: true });
        },
        error: (error) => {
          log.error('Logout failed', error);
        },
        complete: () => {
          this.router.navigate([this.route.snapshot.queryParams['redirect'] || ''], { replaceUrl: true });
        },
      });
  }

  openDialog(): void {
    this.dialog.open(LoginComponent, {
      width: '600px',
      closeOnNavigation: true,
    });
  }

  contactDTmModal(modul: any) {
    this.globalModalService.contactDtmModal({ modul: modul });
  }

  isLoggedIn() {
    return this.credentialsService.isAuthenticated();
  }

  showHeader(): boolean {
    // Get the current URL path
    const currentUrl = this.router.url;

    // Check if the URL path contains form and public
    return !(currentUrl.includes('-form') && currentUrl.includes('public-'));
  }

  languageChange(language: Language) {
    this.translocoService.setActiveLang(language);

    if (this.authenticationService.isAuthenticated()) {
      this.userService
        .updateUserLanguage(language)
        .pipe(take(1))
        .subscribe({
          next: (value) => {
            console.warn('language stored', value);
          },
          error: (err) => {
            Sentry.addBreadcrumb({ message: `could not store language for userId: ${this.user?.id}`, level: 'error' });
            Sentry.captureException(err);
          },
        });
    }
  }

  isInStateOfAnyModule() {
    return (
      this.isInStateProcessFormBuildingCosts() ||
      this.isInStateRebuildForm() ||
      this.isInStateOperatingCostsForm() ||
      this.isInStateLccForm() ||
      this.isInStateBuildingCostsRealestates() ||
      this.isInStateBuildingCostsRealestatesReference()
    );
  }

  isInStateOperatingCostsForm() {
    return this.url.path().includes('operational-form');
  }

  isInStateProcessFormBuildingCosts() {
    return (
      this.url.path().includes('process-form/input') &&
      this.licenseIssue &&
      this.licenseIssue.indexOf('BUILDING_COSTS_AND_DATES') > -1
    );
  }

  isInStatePlanningFoundation() {
    return (
      this.url.path().includes('planning-foundation-form/input') &&
      this.licenseIssue &&
      this.licenseIssue.indexOf('BUILDING_COSTS_AND_DATES') > -1
    );
  }

  isInStateRebuildForm() {
    return this.url.path().includes('rebuild-form');
  }

  isInStateLccForm() {
    return this.url.path().includes('lcc-form');
  }
  isInStateLcaForm() {
    return this.url.path().includes('lca-form');
  }
  isInStateKv2BIM() {
    return this.url.path().includes('kv-to-bim');
  }

  isInStateEffortPlannerForm() {
    return this.url.path().includes('effort-planner-form');
  }

  isInStateBuildingCostsRealestates() {
    return this.url.path() === '/building-costs/realestates';
  }

  isInStateBuildingCostsRealestatesReference() {
    return this.url.path() === '/building-costs/realestates-reference';
  }

  iSshowInsuranceGvbs() {
    return this.url.path() === 'gvbs/input';
  }
  iSshowSmallBuildingGvbs() {
    return this.url.path() === 'small-building-gvbs/input';
  }

  isInStateInsuranceValues() {
    return (
      this.isInStateSmallBuildingValues() ||
      this.isInStateDamageCosts() ||
      this.isInStateInsuranceValuesRealestates() ||
      this.isInStateInsuranceValuesRealestatesReference() ||
      this.isInStateProcessFormInsuranceValues()
    );
  }

  isInStateDamageCosts() {
    return this.isInStateDamageForm();
  }

  isInStateInsuranceValuesRealestates() {
    return this.url.path() === '/insurance/realestates';
  }

  isInStateInsuranceValuesRealestatesReference() {
    return this.url.path() === '/insurance/realestates-reference';
  }
  isInStateSmallBuilding() {
    return this.url.path() === 'landing_page_agv';
  }
  isInStateSmallBuildingValues() {
    return this.url.path().includes('agv-small-building-form/input');
  }

  showInsuranceValuesFn() {
    return !this.showInsuranceValues();
  }

  showSmallBuildingFn() {
    return !this.showSmallBuilding();
  }

  showDamageCostsFn() {
    return !this.showDamageCosts();
  }

  isInStateProcessFormInsuranceValues() {
    return this.url.path().includes('insurance-form/input');
  }

  isInStateDamageForm() {
    return this.url.path().includes('/damage-form/input');
  }

  isInStateAccuracy() {
    return this.url.path().includes('accuracy');
  }

  isInStateAccuracyOperatingCosts() {
    return this.url.path().includes('accuracy_operating_costs');
  }

  hasAnyInsuranceModule() {
    return this.authenticationService.hasAnyModule('BUILDING_INSURANCE_VALUES');
  }
  goToAgv() {
    this.router.navigate(['/agv']);
  }

  hasAnyBuildingCostsModule() {
    return this.authenticationService.hasAnyModule('BUILDING_COSTS_AND_DATES');
    // return Principal.hasAnyBuildingCostsModule();
  }
  showBuildingCosts() {
    return (
      this.isAdminBool ||
      (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('BUILDING_COSTS_AND_DATES') > -1)
    );
  }
  showInsuranceValues() {
    return (
      this.isAdminBool ||
      (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('BUILDING_INSURANCE_VALUES') > -1)
    );
  }
  showOperatingCosts() {
    return this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('OPERATING_COSTS') > -1);
  }
  showRebuildCosts() {
    return this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('REBUILD_COSTS') > -1);
  }
  showSmallBuilding() {
    return (
      this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('SMALL_BUILDING_AGV') > -1)
    );
  }
  showDamageCosts() {
    return this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('DAMAGE_COSTS') > -1);
  }
  showInsuranceGvbs() {
    return (
      this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('INSURANCE_VALUES_GVBS') > -1)
    );
  }
  showSmallBuildingGvbs() {
    return (
      this.isAdminBool || (this.isDefined(this.licenseIssue) && this.licenseIssue.indexOf('SMALL_BUILDING_GVBS') > -1)
    );
  }
  showInsuranceRealEstatesOverview() {
    return (
      this.showInsuranceGvbs() ||
      (this.isDefined(this.licenseIssue) && this.user?.organisationRole?.includes('ORG_OWNER'))
    );
  }

  isGvbsAdminOrHasOtherCalculator() {
    return (
      this.isDefined(this.licenseIssue) &&
      ((this.licenseIssue.indexOf('INSURANCE_VALUES_GVBS') > -1 && this.user?.organisationRole.indexOf('ORG_OWNER')) ||
        this.licenseIssue.indexOf('DAMAGE_COSTS') > -1 ||
        this.licenseIssue.indexOf('BUILDING_INSURANCE_VALUES') > -1)
    );
  }

  isDefined(value: any) {
    return typeof value !== 'undefined';
  }

  routeToCalculatorform(routeString: string) {
    // this.router.navigate(['process-form/input"'])
    this.router.navigate([routeString], {
      replaceUrl: true,
    });
  }

  onClick(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }

  navigateToDebugMode() {
    const dialogRef = this.dialog.open(PayloadDebugComponent, {
      height: '50vh',
      width: '80vh',
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.info('[PayloadDebugComponent] closed');
    });
  }
}
